import React, { useEffect } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { DoctorConsultation } from '../services/DoctorConsultation';
import { PharmacistConsultation } from '../product/PharmacistConsultation';
export type ConsultationType = 'doctor' | 'pharmacist';
interface ConsultationSheetProps {
  type: ConsultationType | null;
  onClose: () => void;
}
const consultationDetails: Record<ConsultationType, {
  title: string;
  description: string;
}> = {
  doctor: {
    title: 'Free Doctor Consultation',
    description: 'Speak with a licensed healthcare professional.'
  },
  pharmacist: {
    title: 'Ask a Pharmacist',
    description: 'Get quick guidance from our pharmacy team.'
  }
};
export function ConsultationSheet({
  type,
  onClose
}: ConsultationSheetProps) {
  const prefersReducedMotion = useReducedMotion();
  useEffect(() => {
    if (!type) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, type]);
  const details = type ? consultationDetails[type] : null;
  return <AnimatePresence data-id="element-2243" data-mp="2qg5le5fy3z2u">
      {type && details && <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-labelledby="consultation-title" data-id="element-2244" data-mp="3e3dqam9gp64c">
          <motion.button type="button" aria-label="Close consultation" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: prefersReducedMotion ? 0 : 0.18,
        ease: [0.22, 1, 0.36, 1]
      }} className="absolute inset-0 h-full w-full cursor-default bg-slate-950/35" data-id="element-2245" data-mp="3j1fa59epp9gv" />

          <div className="absolute inset-x-0 bottom-0 max-h-[88vh] md:inset-x-auto md:bottom-auto md:left-1/2 md:top-1/2 md:w-full md:max-w-2xl md:-translate-x-1/2 md:-translate-y-1/2" data-id="element-2246" data-mp="5px1vdelrle2">
            <motion.div initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={prefersReducedMotion ? {
          opacity: 0
        } : {
          opacity: 0,
          y: 16
        }} transition={{
          duration: prefersReducedMotion ? 0 : 0.24,
          ease: [0.22, 1, 0.36, 1]
        }} className="max-h-[88vh] overflow-y-auto rounded-t-xl bg-white shadow-2xl md:max-h-[calc(100vh-4rem)] md:rounded-lg" data-id="element-2247" data-mp="aliwe2ct0zfw">
            <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-100 bg-white px-5 py-4 md:px-6" data-id="element-2248" data-mp="3j1kq6a767i8o">
              <div data-id="element-2249" data-mp="3ewuxsvbq0lsi">
                <h2 id="consultation-title" className="text-base font-bold text-gray-900 md:text-lg" data-id="element-2250" data-mp="39zuyd39dmhad">
                  {details.title}
                </h2>
                <p className="mt-0.5 text-xs text-gray-500 md:text-sm" data-id="element-2251" data-mp="3519zb75v23ni">{details.description}</p>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1E2B55]/25" data-id="element-2252" data-mp="1kktsi4hkmxea">
                <XIcon className="h-5 w-5" data-id="element-2253" data-mp="11tj1zofrcmmx" />
              </button>
            </div>
            <div className="p-4 md:p-6" data-id="element-2254" data-mp="16qj79x4s9lcz">
              {type === 'doctor' ? <DoctorConsultation data-id="element-2255" data-mp="1asxsgnvxcx7f" /> : <PharmacistConsultation data-id="element-2256" data-mp="1asecvdtqykwk" />}
            </div>
            </motion.div>
          </div>
        </div>}
    </AnimatePresence>;
}